// Section 25 (M5d): the steward's planning model. Per-worker output comes from the real modifier
// pipeline (`num` on the `output` target, evaluated in the context of each season of the
// horizon), so the steward sizes crews with the same numbers the phases use (24.5). Stocks are
// run forward with a small analytic model to size crews cheaply; project() (24.8) then arbitrates
// between candidate plans when something is at risk (choose.ts).
import type { Config } from '../../config';
import { registryFor } from '../../content';
import {
  isChild,
  isRetired as isRetiredPerson,
  lastCompletedReport,
  peopleSorted,
} from '../../core/queries';
import type {
  GameState,
  JobId,
  Person,
  PersonId,
  ReadContext,
  Resource,
  Season,
  SeasonInfo,
} from '../../core/types';
import { num } from '../../phases/shared/value';
import { readContext } from '../context';
import { bedsOf } from './answer';

export interface StewardCtx {
  state: GameState;
  cfg: Config;
  ctx: ReadContext;
  people: Person[];
  pop: number;
  horizon: number;
  /** The season about to be resolved (k = 0) and the ones after it. */
  seasons: SeasonInfo[];
  /** Food demand per season (last season's, or evaluated now). */
  foodDemand: number;
  waterDemand: number;
  rates: Map<string, number>;
  contexts: ReadContext[];
  /** Beds in standing buildings (Section 9). */
  beds: number;
}

/** The season k seasons after `base` (also sizes the founding crew's year, api/assign.ts). */
export function seasonAt(cfg: Config, base: SeasonInfo, k: number): SeasonInfo {
  const order = cfg.seasons.order;
  const n = order.length;
  const index = (base.seasonIndex + k) % n;
  return {
    season: order[index] as Season,
    year: base.year + Math.floor((base.seasonIndex + k) / n),
    turn: base.turn + k,
    seasonIndex: index,
    isYearStart: index === 0,
    isYearEnd: index === n - 1,
  };
}

function sumDemand(
  state: GameState,
  ctx: ReadContext,
  people: readonly Person[],
  resource: 'food' | 'water',
): number {
  const last = lastCompletedReport(state)?.demand[resource];
  if (last !== undefined) {
    let total = 0;
    for (const p of people)
      total += last[p.id] ?? num(ctx, { kind: 'demand', personId: p.id, resource });
    return total;
  }
  let total = 0;
  for (const p of people) total += num(ctx, { kind: 'demand', personId: p.id, resource });
  return total;
}

export function stewardCtx(state: GameState, cfg: Config): StewardCtx {
  const ctx = readContext(state, cfg);
  const people = peopleSorted(state);
  const horizon = cfg.steward.horizon;
  const seasons: SeasonInfo[] = [];
  const contexts: ReadContext[] = [];
  for (let k = 0; k < horizon; k++) {
    const season = seasonAt(cfg, ctx.season, k);
    seasons.push(season);
    contexts.push(k === 0 ? ctx : { ...ctx, season });
  }
  return {
    state,
    cfg,
    ctx,
    people,
    pop: people.length,
    horizon,
    seasons,
    foodDemand: sumDemand(state, ctx, people, 'food'),
    waterDemand: sumDemand(state, ctx, people, 'water'),
    rates: new Map(),
    contexts,
    beds: bedsOf(state, cfg),
  };
}

/** Output of one worker in a job in season k of the horizon (the job's primary unit). */
export function rate(sc: StewardCtx, p: Person, job: JobId, k: number): number {
  if (job === 'rest') return 0;
  const key = `${k}|${job}|${p.id}`;
  const cached = sc.rates.get(key);
  if (cached !== undefined) return cached;
  const ctx = sc.contexts[k] ?? sc.ctx;
  const value = num(ctx, { kind: 'output', personId: p.id, jobId: job });
  sc.rates.set(key, value);
  return value;
}

export function seasonOf(sc: StewardCtx, k: number): Season {
  return (sc.seasons[k] ?? sc.ctx.season).season;
}

/** Section 18 retirement age: people past it are not auto-assigned (the one core query, D). */
export function isRetired(sc: StewardCtx, p: Person): boolean {
  return isRetiredPerson(sc.state, p, sc.cfg);
}

export function isKid(sc: StewardCtx, p: Person): boolean {
  return isChild(sc.cfg, p);
}

/** Producing this season: not sick, injured or held. */
export function ableNow(p: Person): boolean {
  return p.status.sick === null && p.status.injured === null && p.status.inCustody === null;
}

export function capacityOf(sc: StewardCtx, r: Resource): number {
  return sc.state.stocks[r].capacity;
}

export function totalPlots(sc: StewardCtx): number {
  const registry = registryFor(sc.cfg);
  let plots = 0;
  for (const b of Object.values(sc.state.buildings))
    if (b.condition > 0) plots += registry.buildings[b.type].plots;
  return plots;
}

/** Plots planted this year (the farmers tending them keep their plots until the harvest). */
export function plantedPlots(sc: StewardCtx): Record<PersonId, number> {
  return sc.state.plantedPlots;
}

/**
 * Warmth (firewood) wanted in season k: per person-bed by season, Winter scaled by the land.
 * M7: an overcrowded settlement heats only the beds it has (D-27), so the estimate counts the
 * lesser of people and beds.
 */
export function warmthDemand(sc: StewardCtx, k: number): number {
  const season = seasonOf(sc, k);
  const perBed = sc.cfg.needs.warmthPerBed[season];
  const severity = season === 'winter' ? sc.cfg.land.winterSeverity[sc.state.options.land] : 1;
  return Math.min(sc.pop, sc.beds) * perBed * severity;
}

/** Stock after one season: add, consume, then spoil (over capacity spoils at the higher rate). */
export function carry(
  stock: number,
  produced: number,
  consumed: number,
  capacity: number,
  spoilage: number,
  overMul: number,
): { next: number; short: number } {
  const available = stock + produced;
  const short = Math.max(0, consumed - available);
  const left = Math.max(0, available - consumed);
  const within = Math.min(left, capacity);
  const over = left - within;
  const next = within * (1 - spoilage) + over * Math.max(0, 1 - spoilage * overMul);
  return { next, short };
}

/** Adults below the retirement age: the people the steward may assign. */
export function workers(sc: StewardCtx): Person[] {
  return sc.people.filter((p) => !isChild(sc.cfg, p) && !isRetired(sc, p));
}

/**
 * Plots the year's food demand wants, from the best farmer's yield per plot over the horizon
 * (Summer and Autumn harvests) less the seed, capped by the share of workers who may farm.
 */
export function plotsWanted(sc: StewardCtx): number {
  const pool = workers(sc);
  let best: Person | null = null;
  for (const p of pool)
    if (best === null || (p.skills.farmer ?? 0) > (best.skills.farmer ?? 0)) best = p;
  if (best === null) return 0;
  let perPlot = 0;
  for (let k = 0; k < sc.horizon; k++) perPlot += rate(sc, best, 'farmer', k);
  // M7: the rate carries last season's tool; at harvest the farmers hold the ploughs the stock
  // allows (highest output first, Section 10), so plan with the expected plough share.
  const factor = sc.ctx.registry.jobs.farmer.toolFactor ?? 1;
  const hadTool = lastCompletedReport(sc.state)?.toolUse[best.id] ?? false;
  if (!hadTool) {
    const effective = sc.state.stocks.tools.amount * sc.state.toolDurability;
    const share = Math.min(
      1,
      effective / Math.max(1, Math.floor(pool.length * sc.cfg.steward.maxFarmShare)),
    );
    perPlot *= 1 + (factor - 1) * share;
  }
  perPlot -= sc.cfg.jobs.farmer.seedPerPlot;
  const cap =
    Math.floor(pool.length * sc.cfg.steward.maxFarmShare) * sc.cfg.jobs.farmer.plotsPerFarmer;
  if (perPlot <= 0) return cap;
  const yearDemand = sc.foodDemand * sc.cfg.seasons.order.length * sc.cfg.steward.farmMargin;
  return Math.min(cap, Math.ceil(yearDemand / perPlot));
}
